/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { KnownErrorCode } from '@myphx-engineering/instrumentation/react';
import { Alert, FullBleed } from '@myphx-engineering/phx-design-system';
import { useFullNameFieldValidation } from '@myphx-engineering/phx-design-system/hooks';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    ClaimPrescriberOrderForNewPatientDocument,
    GetPrescriberOrderDocument,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getErrorCode } from '../../../util/get-error-code';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';
import { Loader } from '../../common/Loader';

import {
    type VerifyFormDetails,
    VerifyFormProvider,
    useVerifyForm,
    useVerifyFormSchema,
} from './verify/verify-form';
import { VerifyPrescription } from './verify/VerifyPrescription';
import { VerifyPrescriptionNoAttempts } from './verify/VerifyPrescriptionNoAttempts';

const maxVerifyAttempts = 5;

export const VerifyPrescriptionPage = () => {
    const { prescriberOrderId } = useParams();
    if (prescriberOrderId === undefined) {
        throw Error('Prescription ID is required');
    }
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { patient } = usePatientContext();

    if (!patient) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const [failedAttempts, setFailedAttempts] = useState(0);
    const [submissionError, setSubmissionError] = useState<
        'mismatch' | 'other' | null
    >(null);

    const form = useVerifyForm({
        initialValues: {
            firstName: '',
            lastName: '',
            dobMonth: '',
            dobDay: '',
            dobYear: '',
            attest: false,
        },
        schema: useVerifyFormSchema(),
        validateInputOnBlur: true,
    });

    useFullNameFieldValidation(form);

    const { refetch, isComplete: isRefetchComplete } = useRefetchQueries(
        getRefetchQueryForPrescription(prescriberOrderId, patient.id)
    );

    const [ClaimPrescriberOrderForNewPatient] = useMutation(
        ClaimPrescriberOrderForNewPatientDocument
    );

    const claimPrescription = useCallback(
        async (formData: VerifyFormDetails) => {
            const { firstName, lastName, dobMonth, dobDay, dobYear } = formData;
            const month = dobMonth.padStart(2, '0');
            const day = dobDay.padStart(2, '0');
            const year = dobYear;

            try {
                const result = await ClaimPrescriberOrderForNewPatient({
                    variables: {
                        prescriberOrderId: prescriberOrderId,
                        patientDetails: {
                            firstName,
                            lastName,
                            dateOfBirth: `${year}-${month}-${day}`,
                        },
                    },
                });
                if (!result.data?.claimPrescriberOrderForNewPatient.success) {
                    const code =
                        result.data?.claimPrescriberOrderForNewPatient.code;
                    if (code === KnownErrorCode.VerifyDoesNotMatch) {
                        setSubmissionError('mismatch');
                    } else {
                        setSubmissionError('other');
                    }

                    setFailedAttempts((prev) => prev + 1);
                    return;
                }
            } catch (error: unknown) {
                if (error instanceof ApolloError) {
                    if (
                        getErrorCode(error) ===
                        KnownErrorCode.VerifyDoesNotMatch
                    ) {
                        setSubmissionError('mismatch');
                    }
                } else {
                    setSubmissionError('other');
                }

                setFailedAttempts((prev) => prev + 1);
                return;
            }

            await refetch();
        },
        [ClaimPrescriberOrderForNewPatient, prescriberOrderId, refetch]
    );

    useEffect(() => {
        if (isRefetchComplete) {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.details', {
                    prescriberOrderId,
                })
            );
        }
    }, [navigate, isRefetchComplete, prescriberOrderId]);

    const { data, loading } = useQuery(GetPrescriberOrderDocument, {
        variables: {
            prescriberOrderId,
        },
        fetchPolicy: 'no-cache',
    });

    const prescriberOrder = getFragment(data?.prescriberOrderV2);

    if (loading) {
        return <Loader />;
    }
    if (!data || !prescriberOrder) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }
    if (prescriberOrder.__typename !== 'UnclaimedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId,
                })}
            />
        );
    }

    const totalFailureAttempts =
        (prescriberOrder.verificationAttempts ?? 0) + failedAttempts;
    const errorMessage =
        submissionError === null
            ? null
            : submissionError === 'mismatch'
              ? t('verifyPrescription.mismatchError', {
                    count: maxVerifyAttempts - totalFailureAttempts,
                })
              : t('verifyPrescription.error');

    const noAttemptsLeft = totalFailureAttempts >= maxVerifyAttempts;
    const errorType = noAttemptsLeft ? 'error' : 'warning';
    return (
        <>
            {errorMessage && (
                <FullBleed breakpoint="md" color={errorType} spacing="none">
                    <Alert severity={errorType}>{errorMessage}</Alert>
                </FullBleed>
            )}
            {noAttemptsLeft ? (
                <VerifyPrescriptionNoAttempts
                    prescriberOrder={prescriberOrder}
                />
            ) : (
                <VerifyFormProvider form={form}>
                    <VerifyPrescription
                        prescriberOrder={prescriberOrder}
                        claimPrescription={claimPrescription}
                    />
                </VerifyFormProvider>
            )}
        </>
    );
};
